import { objectRecord, parseCognitoOtpChallenge, parseCognitoOtpTokens, requiredString, } from './dto';
function requireEnv(name) {
    const v = import.meta.env[name];
    if (!v)
        throw new Error(`FAIL: missing ${name}`);
    return v;
}
function config() {
    const userPoolId = requireEnv('VITE_COGNITO_USER_POOL_ID');
    const region = userPoolId.split('_')[0];
    if (!region)
        throw new Error('FAIL: VITE_COGNITO_USER_POOL_ID missing region prefix');
    return {
        clientId: requireEnv('VITE_COGNITO_APP_CLIENT_ID'),
        endpoint: `https://cognito-idp.${region}.amazonaws.com/`,
    };
}
export async function requestEmailOtp(emailInput) {
    const email = normalizeEmail(emailInput);
    const body = await callCognito('AWSCognitoIdentityProviderService.InitiateAuth', {
        AuthFlow: 'CUSTOM_AUTH',
        ClientId: config().clientId,
        AuthParameters: {
            USERNAME: email,
        },
    }, 'We could not send a verification code. Try again later.');
    const challengeName = body.ChallengeName;
    if (challengeName !== 'CUSTOM_CHALLENGE') {
        throw new Error('We could not send a verification code. Try again later.');
    }
    return parseCognitoOtpChallenge(email, body.Session);
}
export async function verifyEmailOtp(challenge, code) {
    const body = await callCognito('AWSCognitoIdentityProviderService.RespondToAuthChallenge', {
        ChallengeName: 'CUSTOM_CHALLENGE',
        ClientId: config().clientId,
        Session: challenge.session,
        ChallengeResponses: {
            USERNAME: challenge.email,
            ANSWER: code.trim(),
        },
    }, 'We could not verify that code. Try again.');
    return parseCognitoOtpTokens(objectRecord(body.AuthenticationResult, 'Cognito AuthenticationResult'));
}
// Server-side session kill for logout (design §3.14): revoking the refresh
// token also revokes the access/id tokens issued from it. Token revocation is
// enabled on the deployed app client (scripts/infra/create-auth-app-client.sh).
export async function revokeEmailOtpRefreshToken(refreshToken) {
    await callCognito('AWSCognitoIdentityProviderService.RevokeToken', {
        ClientId: config().clientId,
        Token: refreshToken,
    });
}
export async function refreshCognitoTokens(refreshToken) {
    const body = await callCognito('AWSCognitoIdentityProviderService.GetTokensFromRefreshToken', {
        RefreshToken: refreshToken, ClientId: config().clientId,
    });
    return parseCognitoOtpTokens(body.AuthenticationResult, refreshToken);
}
export const refreshEmailOtpSession = refreshCognitoTokens;
export async function globalSignOut(accessToken) {
    await callCognito('AWSCognitoIdentityProviderService.GlobalSignOut', { AccessToken: accessToken });
}
export async function startPasskeySignIn(email) {
    const username = normalizeEmail(email);
    const message = 'No passkey for this account on this device. Please use an email code.';
    const body = await callCognito('AWSCognitoIdentityProviderService.InitiateAuth', {
        AuthFlow: 'USER_AUTH', ClientId: config().clientId,
        AuthParameters: { USERNAME: username, PREFERRED_CHALLENGE: 'WEB_AUTHN' },
    }, message);
    try {
        if (body.ChallengeName !== 'WEB_AUTHN')
            throw new Error(message);
        const params = objectRecord(body.ChallengeParameters, 'ChallengeParameters');
        return {
            session: requiredString(body.Session, 'Session'),
            username: typeof params.USERNAME === 'string' ? params.USERNAME : username,
            requestOptions: objectRecord(JSON.parse(requiredString(params.CREDENTIAL_REQUEST_OPTIONS, 'Credential options')), 'Credential options'),
        };
    }
    catch {
        throw new Error(message);
    }
}
export async function completePasskeySignIn(input) {
    const body = await callCognito('AWSCognitoIdentityProviderService.RespondToAuthChallenge', {
        ClientId: config().clientId, Session: input.session, ChallengeName: 'WEB_AUTHN',
        ChallengeResponses: { USERNAME: input.username, CREDENTIAL: JSON.stringify(input.credentialJson) },
    }, 'We could not sign you in with that passkey. Please use an email code.');
    return parseCognitoOtpTokens(body.AuthenticationResult);
}
export async function startPasskeyRegistration(accessToken) {
    const body = await callCognito('AWSCognitoIdentityProviderService.StartWebAuthnRegistration', { AccessToken: accessToken });
    return objectRecord(body.CredentialCreationOptions, 'CredentialCreationOptions');
}
export async function completePasskeyRegistration(accessToken, credentialJson) {
    await callCognito('AWSCognitoIdentityProviderService.CompleteWebAuthnRegistration', { AccessToken: accessToken, Credential: credentialJson });
}
export async function listPasskeys(accessToken) {
    const result = [];
    let nextToken;
    do {
        const body = await callCognito('AWSCognitoIdentityProviderService.ListWebAuthnCredentials', {
            AccessToken: accessToken, ...(nextToken ? { NextToken: nextToken } : {}),
        });
        if (!Array.isArray(body.Credentials))
            throw new Error('FAIL: Credentials must be an array');
        result.push(...body.Credentials.map(value => {
            const item = objectRecord(value, 'Credential');
            return { credentialId: requiredString(item.CredentialId, 'CredentialId'), friendlyName: requiredString(item.FriendlyCredentialName, 'FriendlyCredentialName') };
        }));
        nextToken = body.NextToken == null ? undefined : requiredString(body.NextToken, 'NextToken');
    } while (nextToken);
    return result;
}
export async function deletePasskey(accessToken, credentialId) {
    await callCognito('AWSCognitoIdentityProviderService.DeleteWebAuthnCredential', { AccessToken: accessToken, CredentialId: credentialId });
}
async function callCognito(target, payload, publicError) {
    const response = await fetch(config().endpoint, {
        method: 'POST',
        headers: {
            'content-type': 'application/x-amz-json-1.1',
            'x-amz-target': target,
        },
        body: JSON.stringify(payload),
    });
    if (!response.ok) {
        const text = await response.text();
        if (publicError)
            throw new Error(publicError);
        throw new Error(`Cognito auth failed with status ${response.status}: ${cognitoErrorDetailFromText(text, response)}`);
    }
    const body = await response.json();
    return objectRecord(body, 'Cognito response');
}
function cognitoErrorType(text) {
    try {
        const type = JSON.parse(text).__type;
        return typeof type === 'string' ? type : '';
    }
    catch {
        return '';
    }
}
function cognitoErrorDetailFromText(text, response) {
    if (text.trim().length === 0)
        return response.statusText || 'empty response';
    try {
        const body = JSON.parse(text);
        const message = body.message ?? body.Message ?? body.__type;
        if (typeof message === 'string' && message.trim().length > 0)
            return normalizeCognitoError(message);
    }
    catch {
        return normalizeCognitoError(text);
    }
    return normalizeCognitoError(text);
}
function normalizeCognitoError(message) {
    if (message.toLowerCase().includes('incorrect username or password')) {
        return 'Unable to send a verification code for that email.';
    }
    return message;
}
function normalizeEmail(email) {
    const normalized = email.trim().toLowerCase();
    if (!normalized.includes('@'))
        throw new Error('Enter a valid email address.');
    return normalized;
}
//# sourceMappingURL=cognitoOtp.js.map